import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to structure your MobX app for the real world",
  "description": "",
  "published": "2016-11-04T00:00:00.000Z",
  "redirect_from": ["/blog/structure-mobx-app-real-world/swizec/7181"],
  "categories": "Front End, react, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`This article is a spiritual successor to `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/livecoding-25-adding-mobx-vanilla-react-project/swizec/7170"
      }}>{`Adding MobX to a vanilla React project`}</a>{`, inspired by many of `}<a parentName="p" {...{
        "href": "https://medium.com/@mweststrate"
      }}>{`@mwestrase's articles`}</a>{`, and comes from a few weeks of refactoring a big Backbone app into a React+MobX app. It might not be `}<em parentName="p">{`the`}</em>{` way to structure a MobX app, but it's `}<em parentName="p">{`a`}</em>{` way, and it's been working well for me so far.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/Slack-for-iOS-Upload-1-1.jpg",
        "alt": null
      }}></img></p>
    <p>{`Design goals for this architecture are:`}</p>
    <ul>
      <li parentName="ul">{`minimize boilerplate`}</li>
      <li parentName="ul">{`your app is a state machine`}</li>
      <li parentName="ul">{`flexibility to move elements`}</li>
    </ul>
    <p>{`We're going to minimize boilerplate by relying on React contexts and MobX's wonderful `}<inlineCode parentName="p">{`inject`}</inlineCode>{` function. The combination makes your data stores available everywhere in the app without having to do any wiring and passing by props. Component needs to access the state machine? Inject the store.`}</p>
    <p>{`Your designer can reshuffle the page as much as he or she wants, and all you have to do is change where components go. No rewiring of the business logic unless the business logic itself changes. For me, this was the missing piece of the puzzle with React. Using this approach, your components become truly independent, and you can do whatever you want.`}</p>
    <p>{`It's so much fun that I even came to work early once! If you know me, you know how much I like long mornings for myself.`}</p>
    <h2 {...{
      "id": "this-is-how-its-done"
    }}>{`This is how it's done`}</h2>
    <p>{`Contexts and inject give us flexibility and remove boilerplate. What about the state machines?`}</p>
    <p>{`We're going to treat our MobX stores as the only source of truth in the app `}<em parentName="p">{`and`}</em>{` we're going to put actions into them. Actions change state and can be called from anywhere. Putting them in the store reduces boilerplate, makes sure all components have access to them, and lets you see the whole state machine in a single file.`}</p>
    <p>{`This also makes your app easier to test. If your state machine works, everything works. This is because:`}</p>
    <ul>
      <li parentName="ul">{`MobX in strict mode guarantees state only changes in actions`}</li>
      <li parentName="ul">{`React guarantees the DOM is a pure expression of state`}</li>
    </ul>
    <p>{`Yes, MobX works with mutable state and change observers and all that fun stuff. This sounds icky, if you've been listening to the past few years of functional programming propaganda about immutable state.`}</p>
    <p>{`But you know what's cool? You get all the benefits of that anyway. MobX wraps your state changes in getters and setters, but it `}<em parentName="p">{`could`}</em>{` potentially back those with an immutable data structure and actions `}<em parentName="p">{`could`}</em>{` build a changelog for a time traveling debugger.`}</p>
    <p>{`That might be a fun project. I could use a time traveling debugger for MobX ?`}</p>
    <p>{`Models are another piece of the puzzle.`}</p>
    <p>{`Models represent specific objects within your overall data structure. MobX stores are for state your whole app cares about; models are for state that only a specific instance of a specific React component cares about.`}</p>
    <p>{`That sounds like it should go in component state, right? But that’s a bad idea because it makes your stuff harder to test and breaks the state machine ideal.`}</p>
    <p>{`Models are also a great place to put actions that talk to your backend. Save, fetch, update, that sort of stuff.`}</p>
    <hr></hr>
    <h2 {...{
      "id": "lets-build-a-box-of-kakis"
    }}>{`Let’s build a box of kakis`}</h2>
    <p>{`You should think of this as pseudocode.`}</p>
    <p>{`Let's build a box of `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Diospyros_kaki",
        "title": "Japanese persimmons"
      }}>{`kakis`}</a>{`. I have one of those on my kitchen counter right now because they grow in my girlfriend's mom's backyard. Who knew? ¯`}<em parentName="p">{`(ツ)`}</em>{`/¯`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/Slack-for-iOS-Upload-1.jpg",
        "alt": null
      }}></img></p>
    <p>{`A box of kakis can be opened or closed. It can be sealed shut with sticky tape, and it contains kakis. Its state machine looks like this:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/11/Slack-for-iOS-Upload-1-1.jpg",
        "alt": null
      }}></img></p>
    <p>{`Once the box is open, you can add or remove as many kakis as you want. You can close the box from any `}<inlineCode parentName="p">{`N kaki`}</inlineCode>{` state, but you have to open it to add or remove kakis. You can seal it only when it's closed and open it only when it's unsealed.`}</p>
    <h3 {...{
      "id": "box-the-model"
    }}>{`Box, the model`}</h3>
    <p>{`As a MobX store or model, your box looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/models/Box.js

import { observable, computed, action, extendObservable } from "mobx";

export class Box {
  @observable sealed = true;
  @observable closed = true;
  @observable kakis = [];

  constructor(store, initialState) {
    this.store = store;
    extendObservable(this, initialState);
  }

  @computed get canSeal() {
    return this.closed;
  }

  @computed get canOpen() {
    return !this.sealed;
  }

  @computed get canManipulatekakis() {
    return !this.closed;
  }

  @action addkaki() {
    this.kakis.push(new kaki());
  }

  @action takekaki() {
    this.kakis.pop();
  }

  @action open() {
    if (this.canOpen) {
      this.opened = true;
    }
  }

  @action close() {
    this.closed = true;
  }

  @action seal() {
    if (this.canSeal) {
      this.sealed = true;
    }
  }

  @action unseal() {
    this.sealed = false;
  }
}
`}</code></pre>
    <p><inlineCode parentName="p">{`@observable`}</inlineCode>{` is a MobX decorator that makes properties observable so MobX can do its thing. `}<inlineCode parentName="p">{`extendObservable`}</inlineCode>{` is a convenient way to set a bunch of observable values on an object. `}<inlineCode parentName="p">{`@computed`}</inlineCode>{` marks some properties as deriving purely from state so MobX can memoize them, and `}<inlineCode parentName="p">{`@action`}</inlineCode>{` marks methods as actions.`}</p>
    <p>{`Look at the state machine picture. Observables and computeds put together are the circles (states), and actions are the arrows (transitions between states).`}</p>
    <h3 {...{
      "id": "main-app-component"
    }}>{`Main app component`}</h3>
    <p>{`So much work for a box, right? But look what happens when we put this in a React component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/App.js

import { Provider } from "mobx-react";

import { Box } from "./models/Box";

// would normally go in src/stores/...
class MainStore {
  @observable box = null;

  @action getBoxFromMail() {
    this.box = new Box({
      sealed: true,
      closed: true,
      kakis: [new kaki(), new kaki()],
    });
  }
}

class App extends Component {
  mainStore = new MainStore();

  render() {
    const mainStore = this.mainStore;

    return (
      <provider mainstore={mainStore}>
        <div>
          <button onclick={mainStore.getBoxFromMail.bind(mainStore)}>
            Get Mail
          </button>

          <box></box>
        </div>
      </provider>
    );
  }
}
`}</code></pre>
    <p>{`We set up a new `}<inlineCode parentName="p">{`MainStore`}</inlineCode>{`, which holds the main state for our app. Stuff that everyone needs like have you picked up the box of kakis from the mail yet, is your counter black, is your refrigerator running. That sort of thing.`}</p>
    <p>{`Inside the render function, we use `}<inlineCode parentName="p">{`Provider`}</inlineCode>{` to add `}<inlineCode parentName="p">{`mainStore`}</inlineCode>{` to a React context. Any component inside `}<inlineCode parentName="p">{`Provider`}</inlineCode>{` can get access to `}<inlineCode parentName="p">{`mainStore`}</inlineCode>{` if it needs to.`}</p>
    <p>{`The nice thing about bundling actions with your stores and models is that you can use them in `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` handlers. Which means most of your components can be stateless functional components.`}</p>
    <h3 {...{
      "id": "box-the-react-component"
    }}>{`Box, the React component`}</h3>
    <p>{`Let me show you.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Box.js

import { inject, observer } from 'mobx-react';

const SealedOrOpened = observer(({ box }) => (
    if (box.sealed) {
        return 'Sealed and Closed';
    }else if (!box.sealed && !box.opened) {
        return 'You should open the box';
    }else{
        return 'Take some kakis!';
    }
));

// this helper normally goes somewhere else
const If = ({ cond, children }) => cond ? children : null;

const Box = inject('mainStore')(observer(({ mainStore }) => {
    const box = mainStore.box;

    return (
    <div>
        <sealedoropened box={box}>
        <if cond={box.canOpen}>
            <button onclick={box.open.bind(box)}>Open box</button>
        </if>

        <if cond={box.opened}>
            <ul>
                {box.kakis.map(kaki => <kaki>)}
            </kaki></ul>
            <button onclick={box.takekaki.bind(box)}>
                Take a kaki
            </button>
        </if>
    </sealedoropened></div>
    );
));
`}</code></pre>
    <p>{`That's the `}<inlineCode parentName="p">{`Box`}</inlineCode>{` component in spirit. It renders some text telling you what to do with the box and gives you a button that opens it. Yes, I know there should be a button to take off the sticky tape, too.`}</p>
    <p>{`Once you open the box, a list of kakis appears and a button to take them out one by one.`}</p>
    <p>{`Notice that `}<inlineCode parentName="p">{`Box`}</inlineCode>{` is a functional stateless component. You can do that because all the state and all the actions are in the `}<inlineCode parentName="p">{`Box`}</inlineCode>{` MobX model. There’s no need to build local click handlers.`}</p>
    <p>{`You might also notice that we `}<inlineCode parentName="p">{`inject('mainStore')`}</inlineCode>{`'d into the Box component. That lets us move the component to anywhere within the DOM tree inside `}<inlineCode parentName="p">{`Provider`}</inlineCode>{` and it will continue to work. No changes necessary.`}</p>
    <h3 {...{
      "id": "️-all-around"
    }}>{`❤️ all around`}</h3>
    <p>{`Your designer will love the flexibility, and your PM will fart rainbows at how fast you are.`}</p>
    <p>{`Oh, and that `}<inlineCode parentName="p">{`observer`}</inlineCode>{` thingy? That ensures your components re-render when state changes.`}</p>
    <p>{`Next week, I'm going to write about persisting those changes to a backend of some sort. I’m still working on how to make that part effortless and clean :)`}</p>
    <p>{`Maybe I should make a `}<em parentName="p">{`Building Webapps With React and MobX`}</em>{` book ? `}<a parentName="p" {...{
        "href": "mailto:hi@swizec.com"
      }}>{`Tell me`}</a>{` if you're interested.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      